import React, { useContext, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Badge, OverlayTrigger, Tooltip, Popover } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { gql, useQuery, useMutation } from "@apollo/client";
import StarekContext from "../../context/StarekContext";
import EntityLoadingState from "../../components/EntityLoadingState";
import EntityEmptyState from "../../components/EntityEmptyState";
import { EMPTY_MESSAGES } from "../../constants/emptyStateMessages";
import UserSidebar from "./UserSidebar";
import EntityOperationBar from "../../components/EntityOperationBar";
import "../../starek.css";
import ConfirmModal from "../../components/modals/ConfirmModal";
import { usePermissions } from "../../hooks/usePermissions";
import { parseRouteEntityId } from "../../utils";
import { deactivateData, restoreData } from "../../utils/softDelete";

// UI Strings
const UI_STRINGS = {
    PAGE_TITLE: "Users",
    SEARCH_PLACEHOLDER: "Search",
    DELETE_TOOLTIP_HINT: "Please select local users only",
    // Delete confirmation
    DELETE_TITLE: "Delete User(s)?",
    DELETE_CONFIRM: "Delete",
    DELETE_CANCEL: "Cancel",
    // Success messages
    DELETE_SUCCESS: "User(s) deleted successfully",
    // Error messages
    DELETE_ERROR: "Failed to delete user(s)",
    NOT_FOUND: "User not found",
};

// Confirmation messages
const USER_MESSAGES = {
    DELETE_USERS_SINGLE: (name) => `You are about to delete user "${name}".

This action cannot be undone.

Are you sure you wish to continue?`,

    DELETE_USERS_BULK: (count) => `You are about to delete ${count} user(s).

This action cannot be undone.

Are you sure you wish to continue?`,
};

const GET_USERS = gql`
    query Users($where: UserWhereInput) {
        users(where: $where) {
            id
            email
            name
            username
            is_active
            deleted_at
            description
            home_timezone
            current_timezone
            show_time_in_home_timezone
            identities {
                provider
            }
            memberships {
                plant {
                    id
                    name
                }
                role {
                    id
                    name
                }
            }
        }
    }
`;

const UPDATE_MANY_USER = gql`
    mutation UpdateManyUser($data: UserUpdateManyMutationInput!, $where: UserWhereInput) {
        updateManyUser(data: $data, where: $where) {
            count
        }
    }
`;

const DELETE_ONE_USER = gql`
    mutation DeleteOneUser($id: Int!) {
        deleteOneUser(where: { id: $id }) {
            id
        }
    }
`;

const DELETE_MANY_USER = gql`
    mutation DeleteManyUser($ids: [Int!]!) {
        deleteManyUser(where: { id: { in: $ids } }) {
            count
        }
    }
`;

const formatIdentityProviders = (row) =>
    (row?.identities || []).map((i) => i?.provider).filter(Boolean);

const formatMemberships = (row) =>
    (row?.memberships || [])
        .map((m) => {
            const plant = m?.plant?.name || "-";
            const role = m?.role?.name || "-";
            return `${plant}: ${role}`;
        })
        .filter(Boolean);

const isOnlyLocalIdentity = (row) => {
    const providers = formatIdentityProviders(row);
    return (
        providers.length > 0 &&
        providers.every((p) => p === "local-identity-provider")
    );
};

const Users = () => {
    const starekContext = useContext(StarekContext);
    const navigate = useNavigate();
    const { id: routeId } = useParams();
    const routeUserId = parseRouteEntityId(routeId);
    const { can } = usePermissions();
    const canWrite   = can("user", "WRITE");
    const canDestroy = can("user", "DESTROY");

    const [searchParams, setSearchParams] = useSearchParams();

    // View state lives in the URL so a refresh, a deep link or a back-navigation
    // lands on exactly the same list the user was looking at.
    const showInactive = searchParams.get("showInactive") === "1";

    const setShowInactive = (value, options = {}) => {
        const next = new URLSearchParams(searchParams);
        if (value) {
            next.set("showInactive", "1");
        } else {
            next.delete("showInactive");
        }
        setSearchParams(next, options);
    };

    const { data, loading, error, refetch } = useQuery(GET_USERS, {
        fetchPolicy: "network-only",
        variables: { where: showInactive ? {} : { is_active: { equals: true } } },
    });

    const [items, setItems] = useState([]);
    const [filterText, setFilterText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [showWizard, setShowWizard] = useState(false);
    const [routeEditUser, setRouteEditUser] = useState(null);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    // Tracks the route id we already widened the query for, so the retry runs at most once.
    const inactiveRetryRef = useRef(null);
    const usersFromQuery = useMemo(() => data?.users ?? [], [data?.users]);

    const [deleteOneUser, { loading: deletingOne }] = useMutation(
        DELETE_ONE_USER,
        {
            client: starekContext.engineClient,
        },
    );
    const [deleteManyUser, { loading: deletingMany }] = useMutation(
        DELETE_MANY_USER,
        {
            client: starekContext.engineClient,
        },
    );
    const [updateManyUser] = useMutation(UPDATE_MANY_USER, { client: starekContext.engineClient });

    const isDeleting = deletingOne || deletingMany;

    const localOnlySelection =
        selectedRows.length > 0 && selectedRows.every(isOnlyLocalIdentity);
    const canDelete = selectedRows.length > 0 && localOnlySelection;
    const anyNonLocalSelected =
        selectedRows.length > 0 &&
        selectedRows.some((r) => !isOnlyLocalIdentity(r));
    const showDeleteTooltip = anyNonLocalSelected;

    useEffect(() => {
        if (error) starekContext.showError(error);
    }, [error, starekContext]);

    useEffect(() => {
        if (!loading && data?.users) {
            setItems(data.users);
        }
    }, [data, loading]);

    useEffect(() => {
        if (!routeId) {
            setRouteEditUser(null);
            return;
        }

        if (loading) return;

        if (routeUserId == null) {
            starekContext.showError(UI_STRINGS.NOT_FOUND);
            setRouteEditUser(null);
            navigate("/users", { replace: true });
            return;
        }

        const userToEdit = usersFromQuery.find(
            (user) => Number(user?.id) === routeUserId,
        );

        if (!userToEdit) {
            if (!showInactive && inactiveRetryRef.current !== routeUserId) {
                inactiveRetryRef.current = routeUserId;
                const widened = new URLSearchParams(searchParams);
                widened.set("showInactive", "1");
                setSearchParams(widened, { replace: true });
                return;
            }
            starekContext.showError(UI_STRINGS.NOT_FOUND);
            setRouteEditUser(null);
            navigate("/users", { replace: true });
            return;
        }

        setRouteEditUser(userToEdit);
        setSelectedRows((current) =>
            current.length === 1 && Number(current[0]?.id) === routeUserId
                ? current
                : [userToEdit],
        );
        setShowWizard(true);
    }, [
        routeId,
        routeUserId,
        loading,
        usersFromQuery,
        showInactive,
        searchParams,
        setSearchParams,
        navigate,
        starekContext,
    ]);

    const customStyles = {
        headCells: { style: { fontWeight: "bold" } },
        rows: { style: { minHeight: "60px" } },
        cells: {
            style: {
                paddingTop: "12px",
                paddingBottom: "12px",
                whiteSpace: "normal",
                lineHeight: "1.2rem",
            },
        },
    };

    const filteredItems = useMemo(() => {
        const currentUserRecord = items.find(
            (u) => u.id === starekContext.user?.id || String(u.id) === String(starekContext.user?.id)
        );
        const currentUserIsLocal =
            currentUserRecord && isOnlyLocalIdentity(currentUserRecord);

        const visibleItems = currentUserIsLocal
            ? items.filter(
                  (u) =>
                      !formatIdentityProviders(u).includes(
                          "starek-identity-provider",
                      ),
              )
            : items;

        const q = filterText.trim().toLowerCase();
        if (!q) return visibleItems;
        return visibleItems.filter((u) => {
            const membershipsText = formatMemberships(u)
                .join(" ")
                .toLowerCase();
            const plantNames = (u?.memberships || [])
                .map((m) => m?.plant?.name || "")
                .join(" ")
                .toLowerCase();
            return (
                (u.name || "").toLowerCase().includes(q) ||
                (u.email || "").toLowerCase().includes(q) ||
                membershipsText.includes(q) ||
                plantNames.includes(q) ||
                (u.description || "").toLowerCase().includes(q)
            );
        });
    }, [items, filterText, starekContext.user?.id]);

    const handleAdd = () => {
        setRouteEditUser(null);
        setSelectedRows([]);
        setShowWizard(true);
    };

    const handleEdit = () => {
        if (selectedRows.length !== 1) return;
        navigate(`/users/${selectedRows[0].id}`);
    };

    const handleDrawerClose = async () => {
        setShowWizard(false);
        setRouteEditUser(null);
        setSelectedRows([]);
        setToggleCleared((p) => !p);
        if (routeId) {
            navigate("/users", { replace: true });
        }
        await refetch();
    };

    const handleDelete = () => {
        if (selectedRows.length === 0) return;

        const count = selectedRows.length;
        const userName =
            selectedRows[0]?.name || selectedRows[0]?.email || "Unknown";

        setConfirmCfg({
            open: true,
            type: "danger",
            title: UI_STRINGS.DELETE_TITLE,
            content:
                count === 1
                    ? USER_MESSAGES.DELETE_USERS_SINGLE(userName)
                    : USER_MESSAGES.DELETE_USERS_BULK(count),
            confirmText: UI_STRINGS.DELETE_CONFIRM,
            cancelText: UI_STRINGS.DELETE_CANCEL,
            onConfirm: async () => {
                try {
                    const ids = selectedRows.map((u) => Number(u.id));
                    if (ids.length === 1) {
                        await deleteOneUser({ variables: { id: ids[0] } });
                    } else {
                        await deleteManyUser({ variables: { ids } });
                    }
                    setSelectedRows([]);
                    setToggleCleared((p) => !p);
                    await refetch();
                    starekContext?.showSuccess?.(UI_STRINGS.DELETE_SUCCESS);
                } catch (error) {
                    starekContext.showError(
                        error?.message || UI_STRINGS.DELETE_ERROR,
                    );
                } finally {
                    setConfirmCfg({ open: false });
                }
            },
            onClose: () => setConfirmCfg({ open: false }),
        });
    };

    const NameCell = ({ row }) => (
        <span
            role="button"
            className="text-primary"
            onClick={() => navigate(`/users/${row.id}`)}
        >
            {row.name || "-"}
        </span>
    );

    const activeUser = routeEditUser || selectedRows[0] || null;

    const MembershipsCell = ({ row }) => {
        const memberships = formatMemberships(row);
        const firstTwo = memberships.slice(0, 2);
        const extra = memberships.length - firstTwo.length;

        const pop = (
            <Popover id={`membership-pop-${row.id}`}>
                <Popover.Body className="p-2">
                    {memberships.length === 0 ? (
                        <div className="text-muted">No memberships</div>
                    ) : (
                        memberships.map((m, i) => (
                            <div key={i} className="py-1">
                                {m}
                            </div>
                        ))
                    )}
                </Popover.Body>
            </Popover>
        );

        return (
            <div
                className="d-flex align-items-center flex-wrap"
                style={{ gap: 8 }}
            >
                {firstTwo.map((m) => (
                    <Badge key={m} bg="light" text="dark" className="border">
                        {m}
                    </Badge>
                ))}
                {extra > 0 && (
                    <OverlayTrigger
                        trigger="click"
                        placement="right"
                        overlay={pop}
                        rootClose
                    >
                        <Badge
                            bg="light"
                            text="dark"
                            className="border"
                            role="button"
                        >
                            +{extra} more
                        </Badge>
                    </OverlayTrigger>
                )}
            </div>
        );
    };

    const IdentityProvidersCell = ({ row }) => {
        const providers = formatIdentityProviders(row);
        return (
            <div
                className="d-flex align-items-center flex-wrap"
                style={{ gap: 8 }}
            >
                {providers.map((p) => (
                    <Badge key={p} bg="light" text="dark" className="border">
                        {p}
                    </Badge>
                ))}
            </div>
        );
    };

    const columns = [
        {
            name: "Name",
            selector: (r) => r.name || "-",
            sortable: true,
            cell: (row) => <NameCell row={row} />,
            grow: 1.1,
        },
        {
            name: "Email",
            selector: (r) => r.email || "-",
            sortable: true,
            grow: 1.2,
        },
        {
            name: "Memberships",
            selector: (r) => formatMemberships(r).join(", "),
            sortable: false,
            cell: (row) => <MembershipsCell row={row} />,
            grow: 1.6,
        },
        {
            name: "Identity Provider",
            selector: (r) => formatIdentityProviders(r).join(", "),
            sortable: false,
            cell: (row) => <IdentityProvidersCell row={row} />,
            grow: 1.2,
        },
        {
            name: "Description",
            selector: (r) => r.description || "-",
            sortable: false,
            cell: (row) => (
                <div className="text-truncate" style={{ maxWidth: 420 }}>
                    {row.description || "-"}
                </div>
            ),
            grow: 2,
        },
    ];

    const handleToggleActive = async (users, makeActive) => {
        const userId = starekContext?.currentUser?.id ?? starekContext?.user?.id;
        const ids = users.map((u) => u.id);
        try {
            await updateManyUser({
                variables: {
                    data: makeActive ? restoreData() : deactivateData(),
                    where: { id: { in: ids } },
                },
            });
            starekContext.showSuccess(makeActive ? `Restored ${ids.length} user(s)` : `Deactivated ${ids.length} user(s)`);
            setSelectedRows([]);
            setToggleCleared((p) => !p);
            await refetch();
        } catch (err) {
            starekContext.showError(err);
        }
    };

    const userExtendedActions = selectedRows.length > 0 ? [
        {
            label: "Deactivate",
            icon: "fa-ban",
            danger: true,
            disabled: selectedRows.every((r) => !r.is_active),
            onClick: (rows) => handleToggleActive(rows.filter((r) => r.is_active), false),
        },
        {
            label: "Restore",
            icon: "fa-undo",
            disabled: selectedRows.every((r) => r.is_active),
            onClick: (rows) => handleToggleActive(rows.filter((r) => !r.is_active), true),
        },
    ] : [];

    const userConditionalRowStyles = [
        { when: (r) => !r.is_active, style: { opacity: 0.5, backgroundColor: "#f8f9fa" } },
    ];

    if (loading) return <EntityLoadingState icon="fa-user" />;

    return (
        <div className="container-fluid">
            <div className="font-weight-bold mb-3">{UI_STRINGS.PAGE_TITLE}</div>

            <EntityOperationBar
                entity={{ singular: "User", plural: "Users" }}
                allowSearch={true}
                allowAdd={canWrite}
                allowDelete={canDestroy}
                allowEdit={canWrite}
                allowExtendedActions={canWrite && selectedRows.length > 0}
                extendedActions={userExtendedActions}
                allowShowInactive={true}
                showInactive={showInactive}
                onShowInactiveChange={(v) => { setShowInactive(v); setSelectedRows([]); setToggleCleared((p) => !p); }}
                selectedRows={selectedRows}
                isDeleting={isDeleting}
                deleteDisabled={!canDelete}
                deleteTooltip={
                    showDeleteTooltip ? UI_STRINGS.DELETE_TOOLTIP_HINT : null
                }
                onSearch={(text) => setFilterText(text)}
                onAdd={handleAdd}
                onEdit={handleEdit}
                onDelete={handleDelete}
            >
                <EntityOperationBar.View
                    mode="list"
                    icon="fa fa-list"
                    tooltip="List View"
                >
                    <DataTable
                        columns={columns}
                        data={filteredItems}
                        selectableRows
                        onSelectedRowsChange={({ selectedRows }) =>
                            setSelectedRows(selectedRows)
                        }
                        striped
                        clearSelectedRows={toggleCleared}
                        highlightOnHover
                        pointerOnHover
                        onRowClicked={(row) => navigate(`/users/${row.id}`)}
                        responsive
                        pagination
                        dense
                        selectableRowsHighlight
                        selectableRowsNoSelectAll={false}
                        customStyles={customStyles}
                        noDataComponent={<EntityEmptyState icon="fa-user" entityName="User" messages={EMPTY_MESSAGES.users} isFiltered={!!filterText.trim()} onAdd={handleAdd} />}
                        conditionalRowStyles={userConditionalRowStyles}
                    />
                </EntityOperationBar.View>
            </EntityOperationBar>

            <UserSidebar
                show={showWizard}
                mode={activeUser?.id ? "edit" : "add"}
                initialValues={activeUser || {}}
                onClose={handleDrawerClose}
                onFinish={handleDrawerClose}
                onToggleActive={canWrite ? (entity) => handleToggleActive([entity], !entity.is_active) : undefined}
                readOnly={!canWrite}
            />

            <ConfirmModal
                open={confirmCfg.open}
                type={confirmCfg.type || "danger"}
                title={confirmCfg.title}
                content={confirmCfg.content}
                confirmText={confirmCfg.confirmText}
                cancelText={confirmCfg.cancelText}
                loading={isDeleting}
                onConfirm={confirmCfg.onConfirm}
                onClose={confirmCfg.onClose}
            />
        </div>
    );
};

export { Users };
